import { useEffect, useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RiRadioButtonLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

const SoundStations = ({ dataType }) => {
  const login = useSelector((state) => state.login);
  const isCompany = login.isCompany;
  const isLoggedIn = login.accessToken;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const navigateToSoundHistory = useCallback(
    (theRouteUrl) => {
      navigate(`/sound-data/${theRouteUrl}`);
    },
    [navigate]
  );

  const navigateToSoundCharts = useCallback(
    (theRouteUrl) => {
      navigate(`/soundStation-charts/${theRouteUrl}`);
    },
    [navigate]
  );

  const sound = useSelector((state) => state.sound);
  const SPLData = sound.soundData.OSPL;
  const soundLoading = sound.soundLoading;
  const soundError = sound.soundError;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData || [];
  const informationError = information.informationError;
  const informationLoading = information.informationLoading;

  const leqHourly = useSelector((state) => state.leq.leqHourly);
  const leqNighttime = useSelector((state) => state.leq.leqNighttime);
  const leqDaytime = useSelector((state) => state.leq.leqDaytime);
  const leqDaily = useSelector((state) => state.leq.leqDaily);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageData, setPageData] = useState([]);

  const modifyData = useCallback(
    (data) => {
      // Ensure data is an array before mapping
      return Array.isArray(data)
        ? data.map((station) => {
            const specificStationInformation = informationData.find(
              (specificStation) =>
                specificStation.device_id === station.device_id &&
                specificStation.device === "OSPL" &&
                station.is_test !== true
            );
            return { ...station, ...specificStationInformation };
          })
        : []; // Return an empty array if data is not an array
    },
    [informationData]
  );

  // Memoized data with fallback to empty array if data is not valid
  const modifiedSPLData = useMemo(
    () => modifyData(SPLData || []),
    [SPLData, modifyData]
  );

  const modifiedLeqHourlyData = useMemo(
    () => modifyData(leqHourly.data?.data || []),
    [leqHourly.data, modifyData]
  );

  const modifiedLeqDaytimeData = useMemo(
    () => modifyData(leqDaytime.data?.data || []),
    [leqDaytime.data, modifyData]
  );

  const modifiedLeqNighttimeData = useMemo(
    () => modifyData(leqNighttime.data?.data || []),
    [leqNighttime.data, modifyData]
  );

  const modifiedLeqDAILYData = useMemo(
    () => modifyData(leqDaily.data?.data || []),
    [leqDaily.data, modifyData]
  );

  useEffect(() => {
    if (dataType === "spl") {
      setPageData(modifiedSPLData);
      setLoading(soundLoading);
      setError(soundError);
    } else if (dataType === "hourly") {
      setPageData(modifiedLeqHourlyData);
      setLoading(leqHourly.loading);
      setError(leqHourly.error);
    } else if (dataType === "nighttime") {
      setPageData(modifiedLeqNighttimeData);
      setLoading(leqNighttime.loading);
      setError(leqNighttime.error);
    } else if (dataType === "daytime") {
      setPageData(modifiedLeqDaytimeData);
      setLoading(leqDaytime.loading);
      setError(leqDaytime.error);
    } else if (dataType === "daily") {
      setPageData(modifiedLeqDAILYData);
      setLoading(leqDaily.loading);
      setError(leqDaily.error);
    }
  }, [
    dataType,
    modifiedSPLData,
    modifiedLeqHourlyData,
    modifiedLeqNighttimeData,
    modifiedLeqDaytimeData,
    modifiedLeqDAILYData,
    soundLoading,
    soundError,
    leqHourly.loading,
    leqHourly.error,
    leqNighttime.loading,
    leqNighttime.error,
    leqDaytime.loading,
    leqDaytime.error,
    leqDaily.loading,
    leqDaily.error,
    informationData,
  ]);

  const PersianSortComparer = useCallback((a, b) => {
    const provinceA = a.province || ""; // Fallback to empty string
    const provinceB = b.province || "";

    if (provinceA === provinceB) {
      const cityA = a.city || "";
      const cityB = b.city || "";

      if (cityA === cityB) {
        return (a.stationName || "").localeCompare(b.stationName || "");
      }
      return cityA.localeCompare(cityB);
    }
    return provinceA.localeCompare(provinceB);
  }, []);

  const convertToJalali = useCallback((x) => {
    const [date, time] = [x.slice(0, 10), x.slice(11, 19)];
    const jalaliDate = new Intl.DateTimeFormat("fa-IR").format(new Date(date));
    return `${jalaliDate} (${time})`;
  }, []);

  const convertToJalaliDate = (x) => {
    const A = x.slice(0, 10);
    const C = new Date(A);
    const formattedDate = new Intl.DateTimeFormat("fa-IR").format(C);
    return formattedDate;
  };

  // Memoize the final sorted stations list
  const sortedStationsData = useMemo(() => {
    return pageData.slice().sort(PersianSortComparer);
  }, [pageData, PersianSortComparer]);

  useEffect(() => {
    // console.log("Modified data for dataType:", dataType, pageData);
  }, [pageData, dataType]);

  if (loading || informationLoading) {
    return null;
  } else if (error || informationError) {
    return null;
  } else if (
    Array.isArray(sortedStationsData) &&
    sortedStationsData.length > 0
  ) {
    return (
      <div id="stationTable" className="my-5 overflow-x-auto">
        <table
          style={{ width: "90%", maxWidth: "screen" }}
          className="table table-light table-striped mx-auto mx-lg-5"
        >
          <thead>
            <tr>
              {isCompany && <th className="text-center ">سریال</th>}
              {isCompany && <th className="text-center ">دستگاه</th>}
              <th className="text-center d-none d-md-table-cell">استان</th>
              <th className="text-center d-none d-md-table-cell">شهر</th>
              <th className="text-center ">نام ایستگاه</th>
              {isCompany && <th className="text-center ">آخرین داده</th>}
              <th className="text-center ">
                {dataType === "spl" ? "SPL" : "LEQ"}
              </th>
              {isLoggedIn && <th className="text-center"></th>}
            </tr>
          </thead>
          <tbody>
            {sortedStationsData.map((station) =>
              station.is_test !== true ? (
                <tr key={station.slug}>
                  {isCompany && (
                    <td className="text-center ">{station.device_id}</td>
                  )}
                  {isCompany && (
                    <td className="text-center ">{station.device}</td>
                  )}
                  <td className="text-center d-none d-md-table-cell">
                    {station.province}
                  </td>
                  <td className="text-center d-none d-md-table-cell">
                    {station.city}
                  </td>
                  <td className="text-center ">{station.stationName}</td>
                  {isCompany && (
                    <>
                      {station.created_datetime ? (
                        <td className="text-center ">
                          {convertToJalali(station.created_datetime)}
                        </td>
                      ) : station.ccreated_datetime && dataType !== "hourly" ? (
                        <td className="text-center ">
                          {convertToJalaliDate(station.ccreated_datetime)}
                        </td>
                      ) : station.ccreated_datetime ? (
                        <td className="text-center ">
                          {convertToJalali(station.ccreated_datetime)}
                        </td>
                      ) : (
                        ""
                      )}
                    </>
                  )}
                  <td className="text-center ">
                    {station.spl
                      ? Number(station.spl).toFixed(1)
                      : station.leq
                      ? Number(station.leq).toFixed(1)
                      : ""}
                  </td>
                  {isLoggedIn && (
                    <td className="text-center">
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={() => navigateToSoundHistory(station.slug)}
                      >
                        تاریخچه
                      </button>
                    </td>
                  )}
                  {isLoggedIn && (
                    <td className="text-center">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigateToSoundCharts(station.slug)}
                      >
                        نمودارها
                      </button>
                    </td>
                  )}
                </tr>
              ) : (
                ""
              )
            )}
          </tbody>
        </table>
      </div>
    );
  }
};

export default SoundStations;
